import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Copy, Calendar, Plus, Trash2, ChevronDown, ChevronRight, AlertTriangle, PowerOff, CheckCircle2, QrCode, ShoppingCart, Bike, CalendarClock } from 'lucide-react';
import { Toggle } from './Toggle';
import { Branch, DayKey, DayHours, WeeklyHours, MethodHoursMap, SpecialDay, dayKeys, dayLabels, dayShort, defaultHours } from './BranchesContext';
type MethodKey = keyof MethodHoursMap;
const methodMeta: Record<MethodKey, {
  label: string;
  color: string;
  icon: ComponentType<{
    size?: number;
  }>;
}> = {
  dineIn: {
    label: 'Dine-in (Table-side QR)',
    color: 'purple',
    icon: QrCode
  },
  takeout: {
    label: 'Takeout',
    color: 'orange',
    icon: ShoppingCart
  },
  delivery: {
    label: 'Delivery',
    color: 'blue',
    icon: Bike
  },
  orderAhead: {
    label: 'Dine-in (order ahead)',
    color: 'emerald',
    icon: CalendarClock
  }
};
const colorClasses: Record<string, {
  bg: string;
  text: string;
  ring: string;
  iconBg: string;
}> = {
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    ring: 'ring-purple-200',
    iconBg: 'bg-purple-100 text-purple-600'
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    iconBg: 'bg-orange-100 text-orange-600'
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-200',
    iconBg: 'bg-blue-100 text-blue-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    iconBg: 'bg-emerald-100 text-emerald-600'
  }
};
export function BranchHoursSection({
  branch,
  onUpdate
}: {
  branch: Branch;
  onUpdate: (patch: Partial<Branch>) => void;
}) {
  const updateHours = (day: DayKey, patch: Partial<DayHours>) => {
    onUpdate({
      hours: {
        ...branch.hours,
        [day]: {
          ...branch.hours[day],
          ...patch
        }
      }
    });
  };
  const copyToAllDays = (sourceDay: DayKey) => {
    const src = branch.hours[sourceDay];
    const next: WeeklyHours = {} as WeeklyHours;
    dayKeys.forEach(d => {
      next[d] = {
        ...src
      };
    });
    onUpdate({
      hours: next
    });
  };
  return <div className="space-y-4" data-id="element-491">
      {/* Temporary closure */}
      <TemporaryClosureCard closure={branch.temporaryClosure} onUpdate={patch => onUpdate({
      temporaryClosure: {
        ...branch.temporaryClosure,
        ...patch
      }
    })} data-id="element-492" />

      {/* Weekly hours */}
      <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-493">
        <div className="flex items-center gap-2 mb-3" data-id="element-494">
          <div className="w-7 h-7 rounded-md bg-orange-100 text-orange-600 flex items-center justify-center" data-id="element-495">
            <Clock size={14} data-id="element-496" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-497">
            <h4 className="font-bold text-gray-900 text-sm" data-id="element-498">Weekly hours</h4>
            <p className="text-[11px] text-gray-500" data-id="element-499">
              Default open/close times for this branch
            </p>
          </div>
        </div>

        <div className="divide-y divide-gray-100" data-id="element-500">
          {dayKeys.map(day => <DayRow key={day} day={day} dayHours={branch.hours[day]} onChange={patch => updateHours(day, patch)} onCopyToAll={() => copyToAllDays(day)} data-id="element-501" />)}
        </div>
      </div>

      {/* Per-method hours */}
      <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-502">
        <div className="flex items-center gap-2 mb-1" data-id="element-503">
          <div className="w-7 h-7 rounded-md bg-purple-100 text-purple-600 flex items-center justify-center" data-id="element-504">
            <Clock size={14} data-id="element-505" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-506">
            <h4 className="font-bold text-gray-900 text-sm" data-id="element-507">
              Hours per order method
            </h4>
            <p className="text-[11px] text-gray-500" data-id="element-508">
              Override branch hours for specific methods (delivery often ends
              earlier)
            </p>
          </div>
        </div>

        <div className="divide-y divide-gray-100 mt-2" data-id="element-509">
          {(Object.keys(methodMeta) as MethodKey[]).map(key => <MethodHoursRow key={key} methodKey={key} branchHours={branch.hours} methodHours={branch.methodHours[key]} onChange={next => onUpdate({
          methodHours: {
            ...branch.methodHours,
            [key]: next
          }
        })} data-id="element-510" />)}
        </div>
      </div>

      {/* Special days */}
      <SpecialDaysCard days={branch.specialDays} onChange={days => onUpdate({
      specialDays: days
    })} data-id="element-511" />
    </div>;
}
function DayRow({
  day,
  dayHours,
  onChange,
  onCopyToAll
}: {
  day: DayKey;
  dayHours: DayHours;
  onChange: (patch: Partial<DayHours>) => void;
  onCopyToAll: () => void;
}) {
  return <div className="grid grid-cols-[64px_1fr_auto] sm:grid-cols-[80px_1fr_auto] items-center gap-2 py-2" data-id="element-512">
      <div className="text-xs font-bold text-gray-900 uppercase tracking-wide" data-id="element-513">
        {dayShort[day]}
      </div>
      <div className="flex items-center gap-2 min-w-0" data-id="element-514">
        <button type="button" onClick={() => onChange({
        closed: !dayHours.closed
      })} className={`text-[11px] font-bold px-2 py-1 rounded-md transition-colors uppercase tracking-wide flex-shrink-0 ${dayHours.closed ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`} data-id="element-515">
          {dayHours.closed ? 'Closed' : 'Open'}
        </button>
        {!dayHours.closed && <div className="flex items-center gap-1.5 min-w-0" data-id="element-516">
            <input type="time" value={dayHours.open} onChange={e => onChange({
          open: e.target.value
        })} className="text-xs font-semibold tabular-nums px-1.5 py-1 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition w-[88px]" data-id="element-517" />
            <span className="text-xs text-gray-400" data-id="element-518">→</span>
            <input type="time" value={dayHours.close} onChange={e => onChange({
          close: e.target.value
        })} className="text-xs font-semibold tabular-nums px-1.5 py-1 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition w-[88px]" data-id="element-519" />
          </div>}
      </div>
      <button type="button" onClick={onCopyToAll} className="text-[11px] text-gray-400 hover:text-orange-600 transition-colors p-1 rounded-md hover:bg-orange-50 flex items-center gap-1" title="Copy these hours to all days" aria-label="Copy to all days" data-id="element-520">
        <Copy size={12} data-id="element-521" />
        <span className="hidden sm:inline" data-id="element-522">Copy</span>
      </button>
    </div>;
}
function MethodHoursRow({
  methodKey,
  branchHours,
  methodHours,
  onChange
}: {
  methodKey: MethodKey;
  branchHours: WeeklyHours;
  methodHours: WeeklyHours | null;
  onChange: (next: WeeklyHours | null) => void;
}) {
  const meta = methodMeta[methodKey];
  const colors = colorClasses[meta.color];
  const Icon = meta.icon;
  const isCustom = methodHours !== null;
  const [expanded, setExpanded] = useState(false);
  const summary = isCustom ? summarizeHours(methodHours!) : 'Follows branch hours';
  const toggleCustom = () => {
    if (isCustom) {
      onChange(null);
      setExpanded(false);
    } else {
      // Initialize with the current branch hours so the admin can tweak
      const cloned: WeeklyHours = {} as WeeklyHours;
      dayKeys.forEach(d => {
        cloned[d] = {
          ...branchHours[d]
        };
      });
      onChange(cloned);
      setExpanded(true);
    }
  };
  const updateDay = (day: DayKey, patch: Partial<DayHours>) => {
    if (!methodHours) return;
    onChange({
      ...methodHours,
      [day]: {
        ...methodHours[day],
        ...patch
      }
    });
  };
  return <div className="py-2.5" data-id="element-523">
      <div className="flex items-center gap-3" data-id="element-524">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${colors.iconBg}`} data-id="element-525">
          <Icon size={14} data-id="element-526" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-527">
          <div className="font-semibold text-gray-900 text-sm leading-tight" data-id="element-528">
            {meta.label}
          </div>
          <div className={`text-[11px] leading-tight mt-0.5 ${isCustom ? colors.text + ' font-semibold' : 'text-gray-500'}`} data-id="element-529">
            {summary}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-530">
          {isCustom && <button type="button" onClick={() => setExpanded(v => !v)} className="text-[11px] font-semibold text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-50" data-id="element-531">
              {expanded ? 'Hide' : 'Edit'}
              {expanded ? <ChevronDown size={12} data-id="element-532" /> : <ChevronRight size={12} data-id="element-533" />}
            </button>}
          <Toggle checked={isCustom} onChange={toggleCustom} data-id="element-534" />
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-535">
        {isCustom && expanded && methodHours && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-536">
            <div className={`mt-3 ml-11 pl-3 border-l-2 ${colors.ring.replace('ring-', 'border-')} space-y-1`} data-id="element-537">
              {dayKeys.map(day => <DayRow key={day} day={day} dayHours={methodHours[day]} onChange={patch => updateDay(day, patch)} onCopyToAll={() => {
            const src = methodHours[day];
            const next: WeeklyHours = {} as WeeklyHours;
            dayKeys.forEach(d => {
              next[d] = {
                ...src
              };
            });
            onChange(next);
          }} data-id="element-538" />)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function summarizeHours(h: WeeklyHours): string {
  // Group consecutive days with identical hours
  const segments: {
    start: DayKey;
    end: DayKey;
    hours: DayHours;
  }[] = [];
  let curr: {
    start: DayKey;
    end: DayKey;
    hours: DayHours;
  } | null = null;
  for (const day of dayKeys) {
    const dh = h[day];
    if (curr && curr.hours.closed === dh.closed && curr.hours.open === dh.open && curr.hours.close === dh.close) {
      curr.end = day;
    } else {
      if (curr) segments.push(curr);
      curr = {
        start: day,
        end: day,
        hours: dh
      };
    }
  }
  if (curr) segments.push(curr);
  return segments.map(s => {
    const range = s.start === s.end ? dayShort[s.start] : `${dayShort[s.start]}–${dayShort[s.end]}`;
    if (s.hours.closed) return `${range} closed`;
    return `${range} ${s.hours.open}–${s.hours.close}`;
  }).join(' · ');
}
function TemporaryClosureCard({
  closure,
  onUpdate
}: {
  closure: Branch['temporaryClosure'];
  onUpdate: (patch: Partial<Branch['temporaryClosure']>) => void;
}) {
  // closure can be null on a fresh branch — treat that as "not active" and
  // allow toggling the closure on to create a fresh object via onUpdate.
  const active = closure?.active ?? false;
  return <div className={`rounded-xl border p-4 transition-colors ${active ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`} data-id="element-539">
      <div className="flex items-start gap-3" data-id="element-540">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-541">
          {active ? <PowerOff size={16} data-id="element-542" /> : <AlertTriangle size={16} data-id="element-543" />}
        </div>
        <div className="flex-1 min-w-0" data-id="element-544">
          <div className="flex items-center justify-between gap-2" data-id="element-545">
            <h4 className="font-bold text-gray-900 text-sm" data-id="element-546">
              Temporary closure
            </h4>
            <Toggle checked={active} onChange={v => onUpdate({
            active: v
          })} data-id="element-547" />
          </div>
          <p className="text-[11px] text-gray-500 leading-snug mt-0.5" data-id="element-548">
            {active ? 'This branch is currently closed to customers. Orders cannot be placed.' : 'Quickly take this branch offline without changing its hours.'}
          </p>

          <AnimatePresence initial={false} data-id="element-549">
            {active && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-550">
                <div className="space-y-2.5 mt-3" data-id="element-551">
                  <div data-id="element-552">
                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-red-700 mb-1" data-id="element-553">
                      Reason (shown to customers)
                    </label>
                    <input type="text" value={closure.reason} onChange={e => onUpdate({
                  reason: e.target.value
                })} placeholder="e.g. Closed for staff training, back tomorrow" className="w-full px-3 py-2 rounded-lg border border-red-200 bg-white text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition" data-id="element-554" />
                  </div>
                  <div data-id="element-555">
                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-red-700 mb-1" data-id="element-556">
                      Reopens at (optional)
                    </label>
                    <input type="datetime-local" value={closure.until || ''} onChange={e => onUpdate({
                  until: e.target.value || undefined
                })} className="w-full px-3 py-2 rounded-lg border border-red-200 bg-white text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition" data-id="element-557" />
                    <p className="text-[10px] text-red-700/70 mt-1" data-id="element-558">
                      Leave empty to stay closed until you manually re-open.
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}
function SpecialDaysCard({
  days,
  onChange
}: {
  days: SpecialDay[];
  onChange: (days: SpecialDay[]) => void;
}) {
  const addSpecialDay = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const newDay: SpecialDay = {
      id: `s_${Date.now()}`,
      date: `${yyyy}-${mm}-${dd}`,
      label: '',
      closed: true
    };
    onChange([...days, newDay]);
  };
  const updateDay = (id: string, patch: Partial<SpecialDay>) => {
    onChange(days.map(d => d.id === id ? {
      ...d,
      ...patch
    } : d));
  };
  const removeDay = (id: string) => {
    onChange(days.filter(d => d.id !== id));
  };
  // Sort upcoming first by date
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  return <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-559">
      <div className="flex items-center justify-between gap-2 mb-3" data-id="element-560">
        <div className="flex items-center gap-2 min-w-0" data-id="element-561">
          <div className="w-7 h-7 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-562">
            <Calendar size={14} data-id="element-563" />
          </div>
          <div className="min-w-0" data-id="element-564">
            <h4 className="font-bold text-gray-900 text-sm" data-id="element-565">
              Holiday & special hours
            </h4>
            <p className="text-[11px] text-gray-500" data-id="element-566">
              Overrides for specific dates ({days.length})
            </p>
          </div>
        </div>
        <button type="button" onClick={addSpecialDay} className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors px-2 py-1 rounded-md hover:bg-orange-50" data-id="element-567">
          <Plus size={13} data-id="element-568" />
          Add
        </button>
      </div>

      {sorted.length === 0 ? <div className="text-center py-6 text-xs text-gray-400" data-id="element-569">
          No special days yet. Add one for upcoming holidays.
        </div> : <div className="space-y-2" data-id="element-570">
          <AnimatePresence initial={false} data-id="element-571">
            {sorted.map(day => <motion.div key={day.id} layout initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-572">
                <SpecialDayRow day={day} onUpdate={patch => updateDay(day.id, patch)} onRemove={() => removeDay(day.id)} data-id="element-573" />
              </motion.div>)}
          </AnimatePresence>
        </div>}
    </div>;
}
function SpecialDayRow({
  day,
  onUpdate,
  onRemove
}: {
  day: SpecialDay;
  onUpdate: (patch: Partial<SpecialDay>) => void;
  onRemove: () => void;
}) {
  return <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-2.5" data-id="element-574">
      <div className="grid grid-cols-[1fr_auto] gap-2 items-start" data-id="element-575">
        <div className="space-y-2 min-w-0" data-id="element-576">
          <div className="grid grid-cols-[120px_1fr] gap-2" data-id="element-577">
            <input type="date" value={day.date} onChange={e => onUpdate({
            date: e.target.value
          })} className="text-xs font-semibold tabular-nums px-2 py-1.5 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-578" />
            <input type="text" value={day.label} onChange={e => onUpdate({
            label: e.target.value
          })} placeholder="Holiday name (e.g. Christmas)" className="text-xs px-2 py-1.5 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition min-w-0" data-id="element-579" />
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-id="element-580">
            <button type="button" onClick={() => onUpdate({
            closed: !day.closed
          })} className={`text-[10px] font-bold px-2 py-1 rounded-md transition-colors uppercase tracking-wide ${day.closed ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`} data-id="element-581">
              {day.closed ? 'Closed' : 'Open'}
            </button>
            {!day.closed && <div className="flex items-center gap-1.5" data-id="element-582">
                <input type="time" value={day.open || '08:00'} onChange={e => onUpdate({
              open: e.target.value
            })} className="text-xs font-semibold tabular-nums px-1.5 py-1 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition w-[88px]" data-id="element-583" />
                <span className="text-xs text-gray-400" data-id="element-584">→</span>
                <input type="time" value={day.close || '22:00'} onChange={e => onUpdate({
              close: e.target.value
            })} className="text-xs font-semibold tabular-nums px-1.5 py-1 rounded-md border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition w-[88px]" data-id="element-585" />
              </div>}
          </div>
        </div>
        <button type="button" onClick={onRemove} className="text-gray-400 hover:text-red-600 p-1 rounded-md hover:bg-red-50 transition-colors flex-shrink-0" aria-label="Remove special day" data-id="element-586">
          <Trash2 size={14} data-id="element-587" />
        </button>
      </div>
    </div>;
}